import React, { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import RECarousel, { consts } from 'react-elastic-carousel'
import { useSelector } from 'react-redux'
import Icon from 'srcReact/components/icons/Icon'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'

const Carousel = ({
  initialActive,
  enableAutoPlay,
  showArrows,
  pagination,
  verticalMode,
  itemPadding,
  className,
  breakPoints,
  prevArrowClass,
  nextArrowClass,
  prevIconName = 'chevron-left24',
  nextIconName = 'chevron-right24',
  onNextStart,
  onNextEnd,
  onPrevStart,
  onPrevEnd,
  isArrowEdge,
  onChange,
  children,
  afterCarouselElementId
}) => {
  const { t } = useTranslation()
  const carouselRef = useRef(null)
  const carouselWrapperRef = useRef(null)
  const skipLinkRef = useRef(null)
  const {
    common: { isMobile }
  } = useSelector((state) => state)

  useEffect(() => {
    const carouselWrapperElement = carouselWrapperRef.current
    if (carouselWrapperElement) {
      carouselWrapperElement.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      if (carouselWrapperElement) {
        carouselWrapperElement.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [])

  useEffect(() => {
    carouselRef.current && carouselRef.current.goTo(initialActive)
  }, [initialActive])

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (skipLinkRef.current) {
        skipLinkRef.current.focus()
      }
    }
  }

  const handleKeyDownNavigation = (e, direction) => {
    if (e.key === 'Tab') {
      e.preventDefault()
      if (direction === consts.PREV) {
        const visibleElements = document.querySelectorAll('[class*="rec-carousel-item-visible"]')
        if (visibleElements?.length > 0) {
          const firstVisibleElement = visibleElements[0]
          firstVisibleElement.tabIndex = 0
          firstVisibleElement.focus()
        }
      }
      if (direction === consts.NEXT && e.shiftKey) {
        const visibleElements = document.querySelectorAll('[class*="rec-carousel-item-visible"]')
        if (visibleElements?.length > 0) {
          const lastVisibleElement = visibleElements[visibleElements.length - 1]
          lastVisibleElement.tabIndex = 0
          lastVisibleElement.focus()
        }
      }
    }
  }

  const settings = {
    enableAutoPlay,
    showArrows,
    pagination,
    verticalMode,
    itemPadding
  }
  const myArrow = ({ type, onClick, isEdge }) => {
    const pointer =
      type === consts.PREV ? <Icon name={prevIconName} size='large24' /> : <Icon name={nextIconName} size='large24' />
    return (
      <button
        onKeyDown={(e) => handleKeyDownNavigation(e, type)}
        aria-label={
          type === 'consts.PREV'
            ? t('global.prevNavigation', { type: t('global.slides') })
            : t('global.nextNavigation', { type: t('global.slides') })
        }
        onClick={onClick}
        className={`${type === consts.PREV ? prevArrowClass : nextArrowClass} ${styles.arrow} ${
          isEdge ? styles.hiddenArrow : ''
        }`}
        disabled={isArrowEdge ? isEdge : false}>
        {pointer}
      </button>
    )
  }

  return (
    <div className={styles.carouselWithSkipWrapper}>
      <a
        href={afterCarouselElementId}
        className={`${styles.skipLink} is-sr-only is-sr-only-focusable`}
        ref={skipLinkRef}>
        {t('global.skipCarousel')}
      </a>
      <div ref={carouselWrapperRef} className={styles.carouselOuterWrapper}>
        <RECarousel
          {...settings}
          ref={carouselRef}
          className={`${className} ${styles.carouselWrapper}`}
          renderPagination={({ pages, activePage, onClick }) => {
            return (
              <div className={styles.dots}>
                {pages.map((page) => {
                  const isActivePage = activePage === page
                  return (
                    <span
                      key={page}
                      onClick={() => onClick(page)}
                      active={isActivePage}
                      className={`${styles.oval} ${
                        isMobile && (pages.length > 15 ? styles.small : pages.length > 10 ? styles.medium : '')
                      } ${isActivePage && styles.isActive}`}
                    />
                  )
                })}
              </div>
            )
          }}
          breakPoints={breakPoints}
          onChange={onChange}
          renderArrow={myArrow}
          onNextStart={onNextStart}
          onNextEnd={onNextEnd}
          onPrevStart={onPrevStart}
          onPrevEnd={onPrevEnd}
          enableSwipe
          enableMouseSwipe
          showEmptySlots>
          {children}
        </RECarousel>
      </div>
    </div>
  )
}

Carousel.propTypes = {
  enableAutoPlay: PropTypes.bool,
  showArrows: PropTypes.bool,
  pagination: PropTypes.bool,
  verticalMode: PropTypes.bool,
  className: PropTypes.string,
  prevArrowClass: PropTypes.string,
  nextArrowClass: PropTypes.string,
  itemPadding: PropTypes.array,
  breakPoints: PropTypes.array,
  initialActive: PropTypes.number,
  onNextStart: PropTypes.func,
  onNextEnd: PropTypes.func,
  onPrevStart: PropTypes.func,
  onPrevEnd: PropTypes.func,
  onChange: PropTypes.func,
  isArrowEdge: PropTypes.bool,
  children: PropTypes.node.isRequired
}

Carousel.defaultProps = {
  enableAutoPlay: false,
  showArrows: true,
  pagination: false,
  verticalMode: false,
  isArrowEdge: true,
  initialActive: 0,
  itemPadding: [10, 10],
  breakPoints: [
    { width: 1, itemsToShow: 1, itemsToScroll: 1 },
    { width: 550, itemsToShow: 2, itemsToScroll: 2 },
    { width: 850, itemsToShow: 3, itemsToScroll: 3 },
    { width: 1150, itemsToShow: 4, itemsToScroll: 4 },
    { width: 1450, itemsToShow: 5, itemsToScroll: 5 },
    { width: 1750, itemsToShow: 6, itemsToScroll: 6 }
  ]
}

export default Carousel
